<template>
    <div class="space-y-6">
        <envelope-title
            v-model="envelope_title"
            :envelope="local_envelope"
            :creating="creating"
            :toast="toast_target === 'title' ? 'Saved' : null"
        >
        </envelope-title>

        <envelope-documents
            :property_id="property.id"
            :property="property"
            :documents="documents"
            :templates="templates"
            :document_folders="document_folders"
            :envelope="local_envelope"
            :envelope_documents="envelope_documents"
            :toast="toast_target === 'documents' ? 'Saved' : null"
            @documents-updated="handleDocumentsUpdated"
        />

        <envelope-recipients
            :property="property"
            :envelope="local_envelope"
            :broker_will_sign="broker_will_sign"
            :statuses="recipient_statuses"
            :toast="toast_target === 'recipients' || toast_target === 'broker_will_sign' ? 'Saved' : null"
            @recipientsUpdated="handleRecipientsUpdated"
            @brokerWillSignUpdated="handleBrokerWillSignUpdated"
        />

        <envelope-status
            v-if="local_envelope && (! creating)"
            :envelope="local_envelope"
            :envelopes_available="envelopes_available"
            :signature_purchase_route="signature_purchase_route"
            :has_documents="has_documents"
            :has_recipients="recipients_are_valid"
            @launchDocusign="launchDocusign"
            @recipientStatusUpdated="updateRecipientStatus"
        />

        <div v-if="creating"
            class="flex justify-between"
        >
            <div class="flex-none">
                <button-blue-outline @click="$emit('cancel')">
                    Cancel
                </button-blue-outline>
            </div>

            <div class="flex flex-1 justify-end items-center space-x-6 pl-12">
                <button-green @click="saveEnvelope">
                    <span class="px-4">Save Changes</span>
                </button-green>

                <button-blue @click="handleDocusignClick"
                    :disabled="! has_documents || ! recipients_are_valid"
                >
                    <span class="px-4">Send for Signatures</span>
                </button-blue>
            </div>
        </div>

        <div v-else
            class="flex justify-between"
        >
            <div class="flex-none">
                <a :href="signatures_index_route">
                    <button-blue-outline>
                        <i class="fas fa-arrow-left mr-1 -ml-1" />
                        Back
                    </button-blue-outline>
                </a>
            </div>

            <div v-if="! local_envelope.completed_at"
                class="flex flex-1 justify-end items-center space-x-6 pl-12"
            >
                <button-text-red v-if="editable"
                    @click="show_delete_confirmation = true"
                >
                    <i class="far fa-trash-alt mr-1"></i>
                    Delete
                </button-text-red>

                <button-blue @click="handleDocusignClick"
                    :disabled="! has_documents || ! recipients_are_valid"
                >
                    <span class="px-4">{{ editable ? 'Send for Signatures' : 'View on Docusign' }}</span>
                </button-blue>
            </div>

            <div v-if="local_envelope.completed_at"
                class="flex flex-1 justify-end items-center space-x-6 pl-12"
            >
                <button-blue @click="handleDocusignClick"
                    :disabled="! has_documents || ! recipients_are_valid"
                >
                    <span class="px-4">View on Docusign</span>
                </button-blue>
            </div>
        </div>

        <docusign-loading-modal
            :show="loading_docusign"
        />

        <envelope-delete-modal
            v-if="envelope"
            :show="show_delete_confirmation"
            :modal_name="`envelope-delete-modal-${envelope.id}`"
            @deleteEnvelope="deleteEnvelope"
            @closed="show_delete_confirmation = false"
        />

        <broker-completion-required-modal
            v-if="envelope"
            :show="show_broker_completion_modal"
            @closed="show_broker_completion_modal = false"
            @launchDocusign="launchDocusign"
        />

        <envelope-has-template-modal
            :show="show_template_modal"
            @closed="show_template_modal = false"
            @launchDocusign="launchDocusign"
        />
    </div>
</template>

<script>
import BrokerCompletionRequiredModal from './broker-completion-required-modal.vue';
import ButtonBlue from '../../../components/button-blue.vue';
import ButtonBlueOutline from '../../../components/button-blue-outline.vue';
import ButtonGreen from '../../../components/button-green.vue';
import ButtonTextRed from '../../../components/button-text-red.vue';
import DocusignLoadingModal from './docusign-loading-modal.vue';
import EnvelopeDeleteModal from './envelope-delete-modal.vue';
import EnvelopeDocuments from './envelope-documents.vue';
import EnvelopeHasTemplateModal from './envelope-has-template-modal.vue';
import EnvelopeRecipients from './envelope-recipients.vue';
import EnvelopeStatus from './envelope-status.vue';
import EnvelopeTitle from './envelope-title.vue';
import Debounce from '../../../mixins/debounce.js';

export default {
    components: {
        BrokerCompletionRequiredModal,
        ButtonBlue,
        ButtonBlueOutline,
        ButtonGreen,
        ButtonTextRed,
        DocusignLoadingModal,
        EnvelopeDeleteModal,
        EnvelopeDocuments,
        EnvelopeHasTemplateModal,
        EnvelopeRecipients,
        EnvelopeStatus,
        EnvelopeTitle,
    },

    mixins: [
        Debounce,
    ],

    props: {
        property: {},
        documents: {},
        templates: {},
        document_folders: {},
        envelope: {},
        envelope_count: {},
        envelopes_available: {},
        signature_purchase_route: {},
        signatures_create_route: {},
        signatures_index_route: {},
        signatures_destroy_route: {},
        signatures_view_docusign_route: {},
        prompt_for_broker_completion: {},
    },

    data() {
        return {
            local_envelope: this.envelope,
            envelope_title: this.envelope?.title || this.getDefaultTitle(),
            envelope_documents: this.envelope?.property_documents || [],
            envelope_recipients: this.envelope?.recipients || [],
            broker_will_sign: this.property.broker
                ? (this.envelope ? this.envelope.broker_will_sign : true)
                : false,
            loading_docusign: false,
            recipient_statuses: [],
            has_unsaved_changes: false,
            toast_target: null,
            show_delete_confirmation: false,
            show_broker_completion_modal: false,
            show_template_modal: false,
        }
    },

    computed: {
        creating() {
            return ! this.local_envelope;
        },
        local_envelope_id() {
            return this.local_envelope?.id || null;
        },
        editable() {
            return ! this.local_envelope?.sent_at;
        },
        has_documents() {
            return this.envelope_documents?.length > 0;
        },
        has_recipients() {
            return !! this.envelope_recipients?.filter(recipient => recipient.name || recipient.email).length;
        },
        has_templates() {
            return this.envelope_documents?.some(document => document.type === 'template');
        },
        recipients_are_valid() {
            return this.has_recipients
                && this.envelope_recipients
                .every(recipient => recipient.name && this.emailIsValid(recipient.email));
        },
    },

    methods: {
        getDefaultTitle() {
            return 'Envelope #' + (parseInt(this.envelope_count) + 1);
        },
        emailIsValid(email) {
            return String(email)
                .toLowerCase()
                .match(
                  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
                );
        },
        saveEnvelope(redirect = true, autosaved_section = null) {
            if (! this.local_envelope) {
                return this.createEnvelope(redirect);
            }

            let payload = {
                title: this.envelope_title || this.getDefaultTitle(),
                documents: this.envelope_documents,
                recipients: this.envelope_recipients,
                broker_will_sign: this.broker_will_sign,
            };

            if (autosaved_section) {
                // Limit to one key from payload, depending on which section is being updated.
                payload = {[autosaved_section]: payload[autosaved_section]};
            }

            return axios.patch(this.signatures_create_route + '/' + this.local_envelope_id, payload)
                .then(response => {
                    if (redirect) {
                        window.location.href = this.signatures_index_route;
                    }

                    if (autosaved_section) {
                        this.showToast(autosaved_section);
                    }
                }).catch(error => {
                    if (error.response.data.error) {
                        alert(error.response.data.error);
                    }
                }).finally(() => {
                    this.has_unsaved_changes = false;
                });
        },
        createEnvelope(redirect = true) {
            return axios.post(this.signatures_create_route, {
                title: this.envelope_title || this.getDefaultTitle(),
                documents: this.envelope_documents,
                recipients: this.envelope_recipients,
                broker_will_sign: this.broker_will_sign,
            }).then(response => {
                this.local_envelope = response.data;

                if (redirect) {
                    window.location.href = this.signatures_index_route;
                }
            }).catch(error => {
                if (error.response.data.error) {
                    alert(error.response.data.error);
                }
            });
        },
        deleteEnvelope() {
            this.show_delete_confirmation = false;

            if (! this.local_envelope) {
                return;
            }

            const destroyUrl = this.signatures_destroy_route.replace('ENVELOPE_ID', this.local_envelope_id);

            return axios.delete(destroyUrl)
                .then(response => {
                    window.location.href = this.signatures_index_route;
                }).catch(error => {
                    if (error.response?.data?.error) {
                        alert(error.response.data.error);
                    } else if (error.response?.status === 400) {
                        alert('Cannot delete an envelope that has been sent.');
                    } else {
                        alert('An error occurred while deleting the envelope.');
                    }
                });
        },
        handleDocusignClick() {
            if (this.editable && this.has_templates) {
                this.showTemplateModal();
            } else {
                this.launchDocusign();
            }
        },
        showTemplateModal() {
            this.show_template_modal = true;
        },
        launchDocusign() {
            // Show loading modal immediately
            this.loading_docusign = true;
            
            this.saveEnvelope(false).then(() => {
                // Make sure we have a valid envelope ID
                if (! this.local_envelope_id) {
                    this.loading_docusign = false;
                    alert('Failed to create envelope. Please try again.');
                    return;
                }

                // Construct the Docusign URL route
                let docusignRoute;

                if (this.signatures_view_docusign_route) {
                    docusignRoute = this.signatures_view_docusign_route;
                } else {
                    // For new envelopes, construct the route manually
                    const baseUrl = this.signatures_create_route.split('/signatures')[0];
                    docusignRoute = `${baseUrl}/signatures/${this.local_envelope_id}/docusign-url`;
                }

                axios.post(docusignRoute)
                    .then(response => {
                        if (response.data?.docusign_url) {
                            window.location.href = response.data.docusign_url;
                        } else {
                            this.loading_docusign = false;
                            console.log(response);
                        }
                    }).catch(error => {
                        this.loading_docusign = false;

                        if (error.response?.data?.error) {
                            alert(error.response.data.error);
                        } else {
                            console.log(error);
                        }
                    });
            }).catch(error => {
                console.error('Failed to save envelope:', error);
                alert('Failed to save envelope. Please try again.');
            });
        },
        showToast(target) {
            this.toast_target = target;

            setTimeout(() => {
                this.toast_target = null;
            }, 1700)
        },
        updateRecipientStatus(statuses) {
            this.recipient_statuses = statuses;
        },
        handleDocumentsUpdated(documents) {
            this.envelope_documents = documents;
            this.has_unsaved_changes = true;

            // Auto-save document changes when updating
            if (this.local_envelope && ! this.creating) {
                this.saveEnvelope(false, 'documents').catch(error => {
                    console.error('Failed to auto-save: ', error);
                });
            }
        },
        handleRecipientsUpdated(recipients) {
            this.envelope_recipients = recipients;
            this.has_unsaved_changes = true;

            // Auto-save recipient changes when updating
            if (this.local_envelope && ! this.creating) {
                this.saveEnvelope(false, 'recipients').catch(error => {
                    console.error('Failed to auto-save: ', error);
                });
            }
        },
        handleBrokerWillSignUpdated(broker_will_sign) {
            this.broker_will_sign = broker_will_sign;
            this.has_unsaved_changes = true;

            // Auto-save recipient changes when updating
            if (this.local_envelope && ! this.creating) {
                this.saveEnvelope(false, 'broker_will_sign').catch(error => {
                    console.error('Failed to auto-save: ', error);
                });
            }
        },
        handleTitleUpdated(title) {
            // Auto-save title changes when updating
            if (this.local_envelope && ! this.creating) {
                this.saveEnvelope(false, 'title').catch(error => {
                    console.error('Failed to auto-save: ', error);
                });
            }
        },
        cancelChanges() {
            window.location.reload();
        },
    },

    created() {
        this.handleTitleUpdated = this.debounce(this.handleTitleUpdated, 500);
        this.handleRecipientsUpdated = this.debounce(this.handleRecipientsUpdated, 500);

        if (this.envelope && this.prompt_for_broker_completion) {
            this.show_broker_completion_modal = true;

            // Remove the alert trigger so it doesn't recur on reload
            const url = new URL(window.location.href);
            const params = new URLSearchParams(url.search);
            params.delete('needs_broker_completion');

            url.search = params.toString();
            window.history.replaceState({}, '', url);
        }
    },

    watch: {
        envelope_title (value, oldValue) {
            this.has_unsaved_changes = true;
            this.handleTitleUpdated();
        },
    },
}
</script>
